import React from 'react';
import { NotificationPreferences } from '../../services';

/** The seven notification switches, each bound to the backend field it saves. */
export type NotificationRowKey =
  | 'morning_briefing_enabled'
  | 'leave_reminder_enabled'
  | 'change_alert_enabled'
  | 'conflict_alert_enabled'
  | 'plan_digest_enabled'
  | 'publish_outcome_email'
  | 'lock_warning_enabled';

export interface NotificationRow {
  label: string;
  /**
   * What the switch sends, copied from the sender's literal strings (example
   * values fill the sender's placeholders). `null` when nothing sends it.
   */
  sample: string | null;
  /** Plain words for a switch whose sender cannot reach the person today. */
  note: string | null;
}

const PUSH_NOT_SET_UP = "Push notifications aren't set up yet, so this is saved but nothing is sent.";
const BRIEFING_EMAILS_OFF =
  'Briefing emails are switched off for everyone right now, so this is saved but nothing is sent.';

/**
 * Row → backend field, in display order. Samples come from:
 * - morning briefing: `generate_morning_briefing_content`'s subject (notifications/services.py)
 * - time to leave: `format_leave_reminder` (notifications/services.py), sent by push
 * - plan changed: `check_calendar_changes` (notifications/tasks.py), sent by push
 * - plan digest: `send_plan_digests`' subject (planner/tasks.py)
 * - publish outcome: `render_outcome_email`'s subject (plans/publish_outcome_email.py)
 * - lock warning: the `lock_warning` line `send_plan_digests` writes (planner/tasks.py)
 */
export const NOTIFICATION_ROWS: Record<NotificationRowKey, NotificationRow> = {
  morning_briefing_enabled: {
    label: 'Morning briefing',
    sample: '“Your VizDay Morning Briefing - 3 event(s) today”',
    // The sender is off for every user (MORNING_BRIEFING_EMAILS_ENABLED
    // defaults false; notifications/tasks.py) — planner/partner_brief_service.py
    // says so as 'Briefing emails are disabled server-wide.'
    note: BRIEFING_EMAILS_OFF,
  },
  leave_reminder_enabled: {
    label: 'Time to leave',
    sample: '“Time to leave!” Leave now for Dentist at 9:30 AM',
    note: PUSH_NOT_SET_UP,
  },
  change_alert_enabled: {
    label: 'Plan changed',
    sample: '“Event Time Changed” Dinner has been rescheduled',
    note: PUSH_NOT_SET_UP,
  },
  conflict_alert_enabled: {
    label: 'Conflicts',
    sample: null,
    note: 'Nothing sends conflict alerts yet. Your choice is saved for when something does.',
  },
  plan_digest_enabled: {
    label: 'Plan digest',
    sample: '“VizDay: plan locks soon on Lisbon weekend”',
    note: null,
  },
  publish_outcome_email: {
    label: 'Explore publish outcome',
    sample: '“Lisbon weekend is in Explore”',
    note: null,
  },
  lock_warning_enabled: {
    label: 'Lock warning',
    sample: '“plan locks soon”',
    note: "This arrives inside the plan digest, which the Plan digest switch controls. This switch is saved but doesn't change it yet.",
  },
};

export const NOTIFICATION_ROW_KEYS = Object.keys(NOTIFICATION_ROWS) as NotificationRowKey[];

/** The sample and its note as one line of text, for the row's second line. */
export function notificationRowExample(row: NotificationRow): string {
  return [row.sample, row.note].filter(Boolean).join(' · ');
}

/** Off is an outlined switch, on is filled ink (Almanac `.toggle` / `.toggle.off`). */
export function notificationSwitchStyles(on: boolean): { track: React.CSSProperties; knob: React.CSSProperties } {
  return {
    track: on
      ? { backgroundColor: 'var(--text-primary)', boxShadow: 'none' }
      : { backgroundColor: 'transparent', boxShadow: 'inset 0 0 0 2px var(--text-primary)' },
    knob: on
      ? { backgroundColor: 'var(--bg-primary)', boxShadow: 'none' }
      : { backgroundColor: 'var(--text-primary)', boxShadow: 'none' },
  };
}

interface NotificationSettingsProps {
  preferences: NotificationPreferences | null;
  onToggle: (key: keyof NotificationPreferences) => void;
  onTimeChange: (time: string) => void;
  onMinutesChange: (minutes: number) => void;
}

const TRACK: React.CSSProperties = {
  width: 44,
  height: 24,
  borderRadius: 12,
  position: 'relative',
  flexShrink: 0,
  transition: 'background 0.2s',
};

function knobStyle(on: boolean): React.CSSProperties {
  return {
    position: 'absolute',
    top: 4,
    left: on ? 24 : 4,
    width: 16,
    height: 16,
    borderRadius: '50%',
    transition: 'left 0.2s',
  };
}

/**
 * Keeps the native checkbox in the tab order and the accessibility tree while
 * the drawn track stands in for it. Inline, so it overrides Settings.css
 * `.setting-toggle input { display: none }`, which took the control away from
 * keyboard and screen-reader users.
 */
export const VISUALLY_HIDDEN_INPUT: React.CSSProperties = {
  display: 'block',
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  opacity: 0,
};

/** The focus indicator the drawn track shows while its hidden checkbox has focus. */
export const TRACK_FOCUS_RING: React.CSSProperties = {
  outline: 'var(--focus-ring-width) solid var(--focus-ring-color)',
  outlineOffset: 'var(--focus-ring-offset)',
};

/** The row's name: above the sample, at the name's size and colour. */
const NAME_STYLE: React.CSSProperties = { fontSize: '0.95rem', color: 'var(--text-primary)' };

/**
 * The row's sample (Almanac `.notif .ex`): under the name, smaller, in the
 * secondary ink. Inline, because Settings.css `.setting-label span:first-child`
 * would otherwise paint it at the name's size in primary ink.
 */
export const SAMPLE_STYLE: React.CSSProperties = { fontSize: '0.8rem', color: 'var(--text-secondary)' };

/** The settings sheet keeps its own sample size; only the ink changes (11px tertiary is under AA). */
export const SHEET_SAMPLE_STYLE: React.CSSProperties = { color: 'var(--text-secondary)' };

function SwitchTrack({ on, focused }: { on: boolean; focused: boolean }) {
  const styles = notificationSwitchStyles(on);
  return (
    <span aria-hidden="true" style={{ ...TRACK, ...styles.track, ...(focused ? TRACK_FOCUS_RING : {}) }}>
      <span style={{ ...knobStyle(on), ...styles.knob }} />
    </span>
  );
}

/**
 * One switch row on the Settings page: a real, focusable checkbox (named by
 * the row's name, described by its sample) drawn as the Almanac track. Space
 * toggles it natively.
 */
function SwitchRow({
  name,
  description,
  checked,
  onChange,
  rowTestId,
  inputTestId,
}: {
  name: string;
  description: string;
  checked: boolean;
  onChange: () => void;
  rowTestId?: string;
  inputTestId: string;
}) {
  const [focused, setFocused] = React.useState(false);
  const descId = React.useId();
  return (
    <label className="setting-toggle" data-testid={rowTestId} style={{ position: 'relative' }}>
      <span className="setting-label">
        <span style={NAME_STYLE}>{name}</span>
        <span className="setting-desc" id={descId} style={SAMPLE_STYLE}>{description}</span>
      </span>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        aria-label={name}
        aria-describedby={descId}
        style={VISUALLY_HIDDEN_INPUT}
        data-testid={inputTestId}
      />
      <SwitchTrack on={checked} focused={focused} />
    </label>
  );
}

function NotificationToggle({
  field,
  checked,
  onToggle,
}: {
  field: NotificationRowKey;
  checked: boolean;
  onToggle: (key: keyof NotificationPreferences) => void;
}) {
  const row = NOTIFICATION_ROWS[field];
  return (
    <SwitchRow
      name={row.label}
      description={notificationRowExample(row)}
      checked={checked}
      onChange={() => onToggle(field)}
      rowTestId={`notif-row-${field}`}
      inputTestId={`notif-toggle-${field}`}
    />
  );
}

export const NotificationSettings: React.FC<NotificationSettingsProps> = ({
  preferences,
  onToggle,
  onTimeChange,
  onMinutesChange,
}) => {
  if (!preferences) return null;

  const toggle = (field: NotificationRowKey) => (
    <NotificationToggle field={field} checked={Boolean(preferences[field])} onToggle={onToggle} />
  );

  return (
    <section className="settings-section">
      <h2>Notifications</h2>
      <p className="section-hint">How VizDay keeps you informed</p>

      <div className="settings-options">
        {toggle('morning_briefing_enabled')}

        {preferences.morning_briefing_enabled && (
          <div className="setting-sub">
            <label className="time-setting">
              <span>Delivery time</span>
              <input
                type="time"
                value={preferences.morning_briefing_time}
                onChange={(e) => onTimeChange(e.target.value)}
              />
            </label>

            <SwitchRow
              name="Send via email"
              description="Saved for when briefing emails are switched back on"
              checked={preferences.morning_briefing_email ?? false}
              onChange={() => onToggle('morning_briefing_email')}
              inputTestId="notif-toggle-morning_briefing_email"
            />
          </div>
        )}

        {toggle('leave_reminder_enabled')}

        {preferences.leave_reminder_enabled && (
          <div className="setting-sub">
            <label className="time-setting">
              <span>Remind me</span>
              <select
                value={preferences.leave_reminder_minutes ?? 30}
                onChange={(e) => onMinutesChange(parseInt(e.target.value))}
                className="input-field"
              >
                <option value={15}>15 min before</option>
                <option value={30}>30 min before</option>
                <option value={45}>45 min before</option>
                <option value={60}>1 hour before</option>
              </select>
            </label>
          </div>
        )}

        {toggle('change_alert_enabled')}
        {toggle('conflict_alert_enabled')}
      </div>

      <h3 className="section-subheading" style={{ marginTop: '1.5rem' }}>Plan Notifications</h3>
      <div className="settings-options">
        {toggle('plan_digest_enabled')}
        {toggle('publish_outcome_email')}
        {toggle('lock_warning_enabled')}
      </div>
    </section>
  );
};

export default NotificationSettings;
